/* ============================================================================
   McDuffy Quiz v2 — SCREEN REGISTRY (the IA as data)
   ----------------------------------------------------------------------------
   Declarative. The engine composes these into a dynamic FLOW:
     OWNER_SCREENS  → for each dog: DOG_SCREENS → RESULT_SCREENS
   so the dog-detail chapters repeat per dog (mirrors McDuffy's current
   multi-dog quiz: capture all names, quiz each dog, aggregate).

   prompt / intro / info / sub may be a string (token-templated by engine.t)
   or a function(ctx) where ctx = { t, tokens, dog, owner, answers, state }.
   `kind` selects the renderer in the view. Browser global + CommonJS export.
   ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.MQ_SCREENS = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  /* Node-stepper chapters (per-question micro-progress lives inside each). */
  var CHAPTERS = [
    { id: 'you', label: 'You' },
    { id: 'basics', label: 'Basics' },
    { id: 'health', label: 'Health' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'meals', label: 'Meals' },
    { id: 'plan', label: 'Plan' }
  ];

  /* Owner-level screens (asked once, up front). ---------------------------- */
  var OWNER_SCREENS = [
    {
      id: 'welcome', chapter: 'you', kind: 'welcome', required: true,
      prompt: 'Let’s build your dog’s perfect bowl.',
      sub: 'data:introSub'
    },
    {
      /* OPTIONAL (2026-07-03) — a soft yes-ladder primes better as a voluntary
         micro-commitment than as a gate that can lose a fence-sitter. */
      id: 'belief', chapter: 'you', kind: 'belief', required: false, skippable: true,
      prompt: 'One quick thing…', /* rendered as an inline Mad-Libs yes-ladder in the view */
      sub: 'There’s no wrong answer — it just helps us pitch things right.',
      info: { variant: 'expert', heading: 'Wherever you’re starting from', body: 'Fresh food isn’t all-or-nothing. Topping up, switching fully, or just curious — we’ll meet you there.' }
    }
  ];

  /* Per-dog screens (repeat for each dog via engine activeDogIndex). ------- */
  var DOG_SCREENS = [
    /* ----- BASICS ----- */
    {
      id: 'sex', chapter: 'basics', kind: 'single-select', required: true, autoAdvance: true,
      intro: function (c) { return c.tokens.Name ? ('Great to meet you, ' + c.tokens.Name + '!') : 'Great to meet you!'; },
      prompt: function (c) { return 'Is ' + c.tokens.Name + ' a boy or a girl?'; },
      options: [
        { value: 'boy', label: 'Boy', icon: 'sex-boy' },
        { value: 'girl', label: 'Girl', icon: 'sex-girl' }
      ]
    },
    {
      id: 'age', chapter: 'basics', kind: 'dual-number', required: true,
      prompt: function (c) { return 'How old is ' + c.tokens.subj + '?'; },
      fields: [
        { id: 'years', label: 'years', min: 0, max: 30, required: true },
        { id: 'months', label: 'months', min: 0, max: 11, required: false, defaultOnSkip: 0 }
      ],
      info: { variant: 'info', body: function (c) { return 'We tune ' + c.tokens.NamePoss + ' portions as ' + c.tokens.subj + ' grows — puppies and seniors need different amounts.'; } }
    },
    {
      id: 'neutered', chapter: 'basics', kind: 'single-select', required: true, autoAdvance: true,
      prompt: function (c) { return 'Is ' + c.tokens.subj + ' ' + c.tokens.neuterTerm + '?'; },
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unknown', label: 'Not sure' }
      ],
      info: { variant: 'info', heading: 'Good to know',
        body: function (c) { return (c.tokens.neuterTermCap || 'Neutered') + ' dogs often need a little less food — we’ll factor that into ' + c.tokens.NamePoss + ' plan.'; } }
    },
    {
      id: 'breed', chapter: 'basics', kind: 'breed', required: true,
      prompt: function (c) { return 'What breed is ' + c.tokens.Name + '?'; },
      options: [
        { value: 'pure', label: 'Pure breed' },
        { value: 'mixed', label: 'Cross breed' },
        { value: 'unknown', label: 'I’m not sure' }
      ],
      info: { variant: 'info', body: function (c) { return 'Knowing ' + c.tokens.NamePoss + ' breed helps us right-size ' + c.tokens.poss + ' portions and recipe.'; } }
    },
    {
      id: 'weight', chapter: 'basics', kind: 'number-unit', required: true,
      prompt: function (c) { return 'What’s ' + c.tokens.poss + ' most recent weight?'; },
      info: { variant: 'info', body: 'A recent weigh-in (or our estimate) is all we need to start.' }
    },
    /* ----- HEALTH ----- */
    {
      /* Captures conditions AND ingredients to leave out in one screen — the
         separate "avoid preferences" screen was merged in here 2026-07-03. */
      id: 'sensitivities', chapter: 'health', kind: 'multi-select', required: false, skippable: true,
      intro: function (c) { return 'Let’s talk about ' + c.tokens.NamePoss + ' health.'; },
      prompt: function (c) { return 'Any allergies or foods to leave out for ' + c.tokens.Name + '?'; },
      optionsRef: 'HEALTH_CONDITIONS',
      info: { variant: 'info', body: function (c) { return 'We’ll build ' + c.tokens.NamePoss + ' recipe around anything you’d rather skip — a strict allergy or just a preference.'; } }
    },
    {
      id: 'goals', chapter: 'health', kind: 'multi-select', required: false, skippable: true,
      prompt: function (c) { return 'Any goals we should focus on for ' + c.tokens.Name + '?'; },
      optionsRef: 'HEALTH_GOALS',
      info: { variant: 'info', body: function (c) { return 'Every McDuffy recipe is complete and balanced. Pick any areas you’d like us to focus on for ' + c.tokens.Name + '.'; } }
    },
    /* ----- LIFESTYLE ----- */
    {
      id: 'activity', chapter: 'lifestyle', kind: 'slider', required: true,
      intro: function (c) { return 'A bit about ' + c.tokens.NamePoss + ' day-to-day.'; },
      prompt: function (c) { return 'How active is ' + c.tokens.subj + '?'; },
      info: { variant: 'info', body: function (c) { return 'Activity tells us how much food ' + c.tokens.Name + ' needs each day to stay happy and healthy.'; } }
    },
    {
      id: 'weightGoal', chapter: 'lifestyle', kind: 'single-select', required: true,
      prompt: function (c) { return 'Where would you like ' + c.tokens.obj + ' to be?'; },
      optionsRef: 'WEIGHT_GOALS',
      panelAfterSelect: true,
      info: { variant: 'info', body: 'We’ll keep portions right for the goal you choose.' }
    },
    /* ----- MEALS ----- */
    {
      /* The one Meals question. currentBrand (competitive-intel, never used) and
         flavors (never steered the recipe — only printed a summary line) were
         cut 2026-07-03 to shorten the mobile funnel — every question must change
         the plan, price, or copy. */
      id: 'currentFood', chapter: 'meals', kind: 'single-select', required: true, autoAdvance: true,
      intro: function (c) { return 'And finally — ' + c.tokens.NamePoss + ' ideal bowl.'; },
      prompt: function (c) { return 'What does ' + c.tokens.Name + ' eat most days right now?'; },
      optionsRef: 'CURRENT_FOODS',
      info: { variant: 'info', body: 'This helps us plan a smooth switch.' }
    }
  ];

  /* Result screens (asked once, after all dogs). --------------------------- */
  var RESULT_SCREENS = [
    {
      id: 'summary', chapter: 'plan', kind: 'summary', required: false,
      prompt: function (c) { return 'Here’s everything we’ll use to build ' + (c.multi ? 'your dogs’' : (c.tokens.NamePoss)) + ' plan.'; }
    },
    {
      id: 'contact', chapter: 'plan', kind: 'contact', required: true,
      prompt: function (c) { return c.multi ? 'Time to reveal your dogs’ plan.' : ('Time to reveal ' + c.tokens.Name + '’s plan.'); },
      sub: 'Add your email so we can save the plan — then we’ll reveal it. We’ll check we deliver to you, too.',
      info: { variant: 'info', body: 'We only message about your plan and delivery. Marketing is opt-in — stop anytime.' }
    },
    {
      /* The reveal is the PORTIONS + PRICE moment only — recipes, pouches and
         the built box live on the NEXT screen (plan). Never preview a "plan"
         here that the plan screen then contradicts (owner, 2026-07-03). */
      id: 'reveal', chapter: 'plan', kind: 'reveal', required: false,
      prompt: function (c) { return (c.multi ? 'Your dogs’ daily portions are ready.' : (c.tokens.NamePoss + ' daily portion is ready.')); }
    },
    {
      id: 'plan', chapter: 'plan', kind: 'plan', required: false,
      prompt: function (c) { return (c.multi ? 'Your personalized plan.' : (c.tokens.NamePoss + ' personalized plan.')); }
    },
    {
      id: 'review', chapter: 'plan', kind: 'review', required: false,
      prompt: 'Review your first box.'
    }
  ];

  /* Name screen for dogs added mid-flow ("+ Add another dog") — the welcome
     mad-lib only names the dogs declared up front. The engine injects this
     per-dog when dog.addedLater is set; it is NOT part of DOG_SCREENS. */
  var DOGNAME_SCREEN = {
    id: 'dogname', chapter: 'basics', kind: 'dogname', required: true,
    intro: 'Another lucky pup!',
    prompt: 'What’s your new dog’s name?'
  };

  /* Flat lookup by id for goto()/edit links. */
  var BY_ID = {};
  [].concat(OWNER_SCREENS, DOG_SCREENS, RESULT_SCREENS, [DOGNAME_SCREEN]).forEach(function (s) { BY_ID[s.id] = s; });

  return {
    CHAPTERS: CHAPTERS,
    OWNER_SCREENS: OWNER_SCREENS,
    DOG_SCREENS: DOG_SCREENS,
    RESULT_SCREENS: RESULT_SCREENS,
    BY_ID: BY_ID
  };
});
